import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from '@mui/material';
import Home from './Home';
import { recupereStats } from '../Services';
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Icons
import AssignmentIcon from '@mui/icons-material/Assignment';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import WarningIcon from '@mui/icons-material/Warning';
import CreditCardIcon from '@mui/icons-material/CreditCard';

const TableauDeBord = () => {
  const [stats, setStats] = useState({
    newDemandesToday: 0,
    toTreat: 0,
    newClientsToday: 0,
    weeklyDemande: { current: 0, previous: 0, variation: 0 },
    monthlyDemande: { current: 0, previous: 0, variation: 0 },
    yearlyDemande: { current: 0, previous: 0, variation: 0 },
    weeklyChartData: [],
    etatVisaToday: {
      eligibilityToday: 0,
      aTraiterToday: 0,
      enCoursToday: 0
    },
    // Nouvelles statistiques
    formulaireComplet: 0,
    verificationEligibilite: 0,
    enAttenteReceptionDossier: 0,
    attentePaiement: 0,
    aFaire: 0,
    aVerifier: 0,
    attenteVisa: 0,
    dossierEnSouffrance: 0,
    traites: 0,
    totalAll: 0,
    totalVisa: 0,
    totalEvisa: 0,
    // Statistique e-card
    ecardsUrgentes: 0
  });
  const [loading, setLoading] = useState(true);
  const [visaFilter, setVisaFilter] = useState('all');

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await recupereStats(visaFilter);
      setStats(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération du tableau de bord:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // Recharger les statistiques toutes les 30 secondes
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [visaFilter]);

  const formatVariation = (variation) => {
    const sign = variation >= 0 ? '+' : '';
    return `${sign}${variation.toFixed(2)}%`;
  };

  const handleRefresh = () => {
    fetchStats();
  };

  const dateFormatter = (isoDateString) => {
    const dateObj = new Date(isoDateString);
    return dateObj.toLocaleDateString('fr-FR');
  };

  const valueFormatter = (value) => {
    return `${value}`;
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Home />

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Tableau de Bord
          </Typography>
          <Button variant="contained" color="primary" onClick={handleRefresh}>
            Rafraîchir
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Filtres Visa/eVisa/Tous */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Card sx={{ px: 2, py: 1, bgcolor: visaFilter === 'all' ? '#1976d2' : '#f5f5f5' }}>
                <Button
                  variant="text"
                  onClick={() => setVisaFilter('all')}
                  sx={{ color: visaFilter === 'all' ? 'white' : 'inherit' }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">{stats?.totalAll || 0}</Typography>
                    <Typography variant="caption">Tous</Typography>
                  </Box>
                </Button>
              </Card>
              <Card sx={{ px: 2, py: 1, bgcolor: visaFilter === 'visa' ? '#1976d2' : '#f5f5f5' }}>
                <Button
                  variant="text"
                  onClick={() => setVisaFilter('visa')}
                  sx={{ color: visaFilter === 'visa' ? 'white' : 'inherit' }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">{stats?.totalVisa || 0}</Typography>
                    <Typography variant="caption">Visa</Typography>
                  </Box>
                </Button>
              </Card>
              <Card sx={{ px: 2, py: 1, bgcolor: visaFilter === 'evisa' ? '#1976d2' : '#f5f5f5' }}>
                <Button
                  variant="text"
                  onClick={() => setVisaFilter('evisa')}
                  sx={{ color: visaFilter === 'evisa' ? 'white' : 'inherit' }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">{stats?.totalEvisa || 0}</Typography>
                    <Typography variant="caption">eVisa</Typography>
                  </Box>
                </Button>
              </Card>
            </Box>

            {/* Statistiques principales avec couleurs */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {/* Formulaire complet */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#2e7d32', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AssignmentIcon />
                      <Box>
                        <Typography variant="h4">{stats?.formulaireComplet || 0}</Typography>
                        <Typography variant="body2">Complet</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Vérification éligibilité */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#0288d1', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VerifiedUserIcon />
                      <Box>
                        <Typography variant="h4">{stats?.verificationEligibilite || 0}</Typography>
                        <Typography variant="body2">Vérif. éligibilité</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* En attente réception dossier */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#9c27b0', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalShippingIcon />
                      <Box>
                        <Typography variant="h4">{stats?.enAttenteReceptionDossier || 0}</Typography>
                        <Typography variant="body2">Attente réception</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Attente paiement */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#ff5722', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PaymentIcon />
                      <Box>
                        <Typography variant="h4">{stats?.attentePaiement || 0}</Typography>
                        <Typography variant="body2">Attente paiement</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* À faire */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#ff9800', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PendingActionsIcon />
                      <Box>
                        <Typography variant="h4">{stats?.aFaire || 0}</Typography>
                        <Typography variant="body2">À faire</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* À vérifier */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#9c27b0', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VerifiedUserIcon />
                      <Box>
                        <Typography variant="h4">{stats?.aVerifier || 0}</Typography>
                        <Typography variant="body2">À vérifier</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Attente visa */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#03a9f4', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HourglassEmptyIcon />
                      <Box>
                        <Typography variant="h4">{stats?.attenteVisa || 0}</Typography>
                        <Typography variant="body2">Attente visa</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Dossier en souffrance */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#ff9800', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon />
                      <Box>
                        <Typography variant="h4">{stats?.dossierEnSouffrance || 0}</Typography>
                        <Typography variant="body2">Dossier en souffrance</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Traités */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#4caf50', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon />
                      <Box>
                        <Typography variant="h4">{stats?.traites || 0}</Typography>
                        <Typography variant="body2">Traités</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* E-cards urgentes */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#ff6b35', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CreditCardIcon />
                      <Box>
                        <Typography variant="h4">{stats?.ecardsUrgentes || 0}</Typography>
                        <Typography variant="body2">E-cards urgentes</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Demandes à traiter (Formulaire complet + payé) */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#e91e63', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptIcon />
                      <Box>
                        <Typography variant="h4">{stats?.toTreat || 0}</Typography>
                        <Typography variant="body2">À traiter (payé)</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Section KPI du jour */}
            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              Statistiques du jour
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Nouvelles demandes</Typography>
                    <Typography variant="h4" color="primary">
                      {stats.newDemandesToday}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Nouveaux clients</Typography>
                    <Typography variant="h4" color="primary">
                      {stats.newClientsToday}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">En cours aujourd'hui</Typography>
                    <Typography variant="h4" color="primary">
                      {stats.etatVisaToday.enCoursToday}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Comparatif (Semaine, Mois, Année) */}
            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              Évolution des demandes
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Semaine</Typography>
                    <Typography variant="h4">{stats.weeklyDemande.current}</Typography>
                    <Typography variant="body1">
                      Semaine précédente : {stats.weeklyDemande.previous}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: stats.weeklyDemande.variation >= 0 ? '#4caf50' : '#f44336' }}
                    >
                      {formatVariation(stats.weeklyDemande.variation)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Mois</Typography>
                    <Typography variant="h4">{stats.monthlyDemande.current}</Typography>
                    <Typography variant="body1">
                      Mois précédent : {stats.monthlyDemande.previous}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: stats.monthlyDemande.variation >= 0 ? '#4caf50' : '#f44336' }}
                    >
                      {formatVariation(stats.monthlyDemande.variation)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Année</Typography>
                    <Typography variant="h4">{stats.yearlyDemande.current}</Typography>
                    <Typography variant="body1">
                      Année précédente : {stats.yearlyDemande.previous}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: stats.yearlyDemande.variation >= 0 ? '#4caf50' : '#f44336' }}
                    >
                      {formatVariation(stats.yearlyDemande.variation)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Graphique évolution 7 derniers jours */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Évolution des demandes (7 derniers jours)
                </Typography>
                <Box sx={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <AreaChart data={stats.weeklyChartData}>
                      <defs>
                        <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#1976d2" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(dateStr) => dateFormatter(dateStr)}
                      />
                      <YAxis />
                      <Tooltip
                        labelFormatter={(label) => dateFormatter(label)}
                        formatter={(value) => valueFormatter(value)}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#1976d2"
                        fillOpacity={1}
                        fill="url(#colorGradient)"
                        name="Demandes"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </Box>
  );
};

export default TableauDeBord;